<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      phoneNumber: '',
      createdInbox: null,
      qrUrl: '',
      qrImageUrl: '',
      qrImageObjectUrl: '',
      qrImageLoading: false,
      qrImageFailed: false,
      pollTimer: null,
      qrRefreshTimer: null,
      qrRefreshIntervalMs: 8000,
      pollAttempts: 0,
      maxPollAttempts: 100,
      confirmAccountAge: false,
      confirmNoSpam: false,
      confirmReadRisks: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    showQr() {
      return (
        this.createdInbox &&
        this.createdInbox?.telegram_user_channel_status !== 'active'
      );
    },
    canProceedToPhone() {
      return this.confirmAccountAge && this.confirmNoSpam && this.confirmReadRisks;
    },
  },
  validations: {
    phoneNumber: { required },
  },
  beforeUnmount() {
    this.stopTimers();
    if (this.qrImageObjectUrl) {
      URL.revokeObjectURL(this.qrImageObjectUrl);
    }
  },
  methods: {
    async generateQr() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      try {
        const payload = await this.$store.dispatch('inboxes/createChannel', {
          channel: {
            type: 'telegram_user',
            phone_number: this.phoneNumber,
          },
        });
        this.createdInbox = payload;
        this.qrUrl = payload.qr_url || '';
        this.qrImageUrl = this.buildQrImageUrl(this.qrUrl, payload.qr_image_url || '');
        this.qrImageFailed = false;
        this.startPolling();
        this.startQrRefresh();
        this.$nextTick(() => this.loadQrImage());
        this.$nextTick(() => this.checkChannelStatus());
      } catch (error) {
        useAlert(
          error?.message ||
            this.$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.ERROR_MESSAGE')
        );
      }
    },
    startPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollAttempts = 0;
      this.pollTimer = setInterval(this.checkChannelStatus, 3000);
    },
    startQrRefresh() {
      // The login token rotates server-side (~every 30s); without re-fetching
      // the image the operator scans a frozen, expired QR (AUTH_TOKEN_EXPIRED).
      if (this.qrRefreshTimer) clearInterval(this.qrRefreshTimer);
      this.qrRefreshTimer = setInterval(this.loadQrImage, this.qrRefreshIntervalMs);
    },
    stopTimers() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
      if (this.qrRefreshTimer) clearInterval(this.qrRefreshTimer);
      this.qrRefreshTimer = null;
    },
    async checkChannelStatus() {
      if (!this.createdInbox?.id) return;
      this.pollAttempts += 1;
      if (this.pollAttempts > this.maxPollAttempts) {
        this.stopTimers();
        useAlert(this.$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.POLL_TIMEOUT'));
        return;
      }
      try {
        const { data } = await InboxesAPI.show(this.createdInbox.id);
        this.createdInbox = data;
        this.qrUrl = data?.qr_url || this.qrUrl;
        this.qrImageUrl = this.buildQrImageUrl(this.qrUrl, data?.qr_image_url || '');
        if (data?.telegram_user_channel_status === 'active') {
          this.stopTimers();
          router.replace({
            name: 'settings_inboxes_add_agents',
            params: { page: 'new', inbox_id: this.createdInbox.id },
          });
        }
      } catch {
        // ignore
      }
    },
    goToAgents() {
      this.stopTimers();
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: this.createdInbox.id },
      });
    },
    refreshQr() {
      this.qrImageFailed = false;
      this.pollAttempts = 0;
      this.loadQrImage();
      this.checkChannelStatus();
    },
    async loadQrImage() {
      if (!this.createdInbox?.id) return;
      // Skip if a fetch is already in flight (the refresh timer can overlap a
      // manual refresh); swap the object URL only once the new blob is ready so
      // the on-screen QR never blanks/flickers between rotations.
      if (this.qrImageLoading) return;
      this.qrImageLoading = true;
      try {
        const { data } = await InboxesAPI.getTelegramUserQrImage(
          this.createdInbox.id
        );
        const nextObjectUrl = URL.createObjectURL(data);
        if (this.qrImageObjectUrl) URL.revokeObjectURL(this.qrImageObjectUrl);
        this.qrImageObjectUrl = nextObjectUrl;
        this.qrImageFailed = false;
      } catch {
        this.qrImageFailed = true;
      } finally {
        this.qrImageLoading = false;
      }
    },
    buildQrImageUrl(qrUrl, qrImageUrl, forceRefresh = false) {
      const baseUrl = qrImageUrl || this.rawQrUrl(qrUrl);
      if (!baseUrl) return '';
      if (!forceRefresh) return baseUrl;
      return this.addTimestamp(baseUrl);
    },
    rawQrUrl(qrUrl) {
      if (!qrUrl) return '';
      const separator = qrUrl.includes('?') ? '&' : '?';
      return `${qrUrl}${separator}raw=1`;
    },
    addTimestamp(url) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}_ts=${Date.now()}`;
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.DESC')"
    />

    <div v-if="!canProceedToPhone && !createdInbox" class="mb-6 space-y-4">
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.DISCLAIMER') }}
      </p>
      <div class="space-y-3 rounded-lg border border-n-weak bg-n-surface-2 p-4">
        <div>
          <p class="mb-1 font-medium text-n-green-11">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RISK_MINIMAL_TITLE') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RISK_MINIMAL_BODY') }}
          </p>
        </div>
        <div>
          <p class="mb-1 font-medium text-n-amber-11">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RISK_MODERATE_TITLE') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RISK_MODERATE_BODY') }}
          </p>
        </div>
        <div>
          <p class="mb-1 font-medium text-n-ruby-11">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RISK_HIGH_TITLE') }}
          </p>
          <p class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.RISK_HIGH_BODY') }}
          </p>
        </div>
      </div>
      <p class="text-sm font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CONFIRM_HEADING') }}
      </p>
      <div class="space-y-3">
        <label class="flex cursor-pointer items-start gap-3">
          <input
            v-model="confirmAccountAge"
            type="checkbox"
            class="mt-1 rounded border-n-slate-8"
          />
          <span class="text-sm text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CONFIRM_ACCOUNT_AGE') }}
          </span>
        </label>
        <label class="flex cursor-pointer items-start gap-3">
          <input
            v-model="confirmNoSpam"
            type="checkbox"
            class="mt-1 rounded border-n-slate-8"
          />
          <span class="text-sm text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CONFIRM_NO_SPAM') }}
          </span>
        </label>
        <label class="flex cursor-pointer items-start gap-3">
          <input
            v-model="confirmReadRisks"
            type="checkbox"
            class="mt-1 rounded border-n-slate-8"
          />
          <span class="text-sm text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.CONFIRM_READ_RISKS') }}
          </span>
        </label>
      </div>
    </div>

    <form
      v-show="canProceedToPhone || createdInbox"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="generateQr()"
      @keydown.enter.prevent
    >
      <div v-if="canProceedToPhone || createdInbox" class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$?.phoneNumber?.$error }">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE_LABEL') }}
          <input
            v-model="phoneNumber"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.PHONE_PLACEHOLDER')"
            :disabled="!!createdInbox"
            @blur="v$?.phoneNumber?.$touch"
          />
        </label>
      </div>

      <div v-if="(canProceedToPhone || createdInbox) && !createdInbox" class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.GENERATE_QR_BUTTON')"
        />
      </div>
    </form>

    <div
      v-if="showQr"
      v-show="canProceedToPhone || createdInbox"
      class="mt-6"
    >
      <p class="mb-2 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SCAN_QR_HINT') }}
      </p>
      <p class="mb-2 text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.SCAN_QR_PATH') }}
      </p>
      <div
        v-if="qrImageLoading"
        class="flex h-48 w-48 items-center justify-center rounded-lg border border-n-weak bg-n-surface-1 text-sm text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.LOADING_QR') || 'Загрузка QR…' }}
      </div>
      <a
        v-else
        :href="qrImageObjectUrl || '#'"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-block rounded-lg border border-n-weak bg-n-surface-1 p-4"
      >
        <img
          v-if="qrImageObjectUrl && !qrImageFailed"
          :src="qrImageObjectUrl"
          alt="QR"
          class="h-48 w-48 object-contain"
          @error="qrImageFailed = true"
        />
        <span v-else class="flex h-48 w-48 items-center justify-center text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.OPEN_QR_LINK') }}
        </span>
      </a>
      <p class="mt-2 text-xs text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.WAITING_SCAN') }}
      </p>
      <div class="mt-3">
        <a
          href="#"
          role="button"
          class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-weak bg-n-surface-2 px-3 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-focus no-underline"
          @click.prevent="refreshQr"
        >
          {{ $t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.REFRESH_QR_BUTTON') }}
        </a>
      </div>
    </div>

    <div
      v-if="createdInbox?.telegram_user_channel_status === 'active'"
      v-show="canProceedToPhone || createdInbox"
      class="w-full mt-4"
    >
      <NextButton
        type="button"
        solid
        blue
        :label="$t('INBOX_MGMT.ADD.TELEGRAM_USER_CHANNEL.GO_TO_AGENTS')"
        @click="goToAgents"
      />
    </div>
  </div>
</template>
